import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api, xcg } from "@/lib/api";
import { StatusBadge, Modal, Field, TextArea, Select, Button } from "@/components/ui-kit";
import { toast } from "sonner";
import { ArrowLeft, Plus, EnvelopeSimple, Trash, ArrowDown, ArrowUp, BellRinging, MapPin } from "@phosphor-icons/react";

export default function CustomerDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [c, setC] = useState(null);
  const [tab, setTab] = useState("balances");
  const [invOpen, setInvOpen] = useState(false);
  const [payOpen, setPayOpen] = useState(null);
  const [emailOpen, setEmailOpen] = useState(false);
  const [remOpen, setRemOpen] = useState(false);

  const [inv, setInv] = useState({ invoice_number: "", description: "", amount: "", due_date: "" });
  const [pay, setPay] = useState({ amount: "", note: "" });
  const [email, setEmail] = useState({ direction: "received", subject: "", body: "", contact: "" });
  const [rem, setRem] = useState({ title: "", kind: "follow_up", due_date: "", note: "" });

  const load = useCallback(() => api.get(`/customers/${id}`).then(({ data }) => setC(data)), [id]);
  useEffect(() => {
    load();
  }, [load]);

  if (!c) return <div className="p-8 font-mono text-sm text-slate-500">Loading…</div>;

  const saveInvoice = async (e) => {
    e.preventDefault();
    try {
      await api.post("/invoices", {
        customer_id: id,
        invoice_number: inv.invoice_number,
        description: inv.description,
        amount: parseFloat(inv.amount),
        due_date: inv.due_date || null,
      });
      toast.success("Invoice created");
      setInvOpen(false);
      setInv({ invoice_number: "", description: "", amount: "", due_date: "" });
      load();
    } catch {
      toast.error("Failed to create invoice");
    }
  };

  const savePayment = async (e) => {
    e.preventDefault();
    try {
      await api.post(`/invoices/${payOpen.id}/payments`, { amount: parseFloat(pay.amount), note: pay.note });
      toast.success("Payment recorded");
      setPayOpen(null);
      setPay({ amount: "", note: "" });
      load();
    } catch {
      toast.error("Failed to record payment");
    }
  };

  const saveEmail = async (e) => {
    e.preventDefault();
    try {
      await api.post("/email-logs", { customer_id: id, ...email });
      toast.success("Email logged");
      setEmailOpen(false);
      setEmail({ direction: "received", subject: "", body: "", contact: "" });
      load();
    } catch {
      toast.error("Failed to log email");
    }
  };

  const saveReminder = async (e) => {
    e.preventDefault();
    try {
      await api.post("/reminders", { ...rem, customer_id: id });
      toast.success("Reminder set");
      setRemOpen(false);
      setRem({ title: "", kind: "follow_up", due_date: "", note: "" });
    } catch {
      toast.error("Failed to set reminder");
    }
  };

  const delInvoice = async (iid) => {
    await api.delete(`/invoices/${iid}`);
    toast.success("Invoice deleted");
    load();
  };

  const delEmail = async (eid) => {
    await api.delete(`/email-logs/${eid}`);
    toast.success("Log deleted");
    load();
  };

  return (
    <div>
      <div className="bg-white/80 backdrop-blur border-b border-slate-200 px-8 py-6 sticky top-0 z-20">
        <button data-testid="back-button" onClick={() => navigate("/customers")} className="flex items-center gap-1 text-sm font-semibold text-slate-500 hover:text-[#16205A] mb-3 transition-colors">
          <ArrowLeft size={16} weight="bold" /> Customers
        </button>
        <div className="flex items-end justify-between gap-4 flex-wrap">
          <div>
            <h1 className="font-heading font-extrabold text-3xl lg:text-4xl leading-none text-[#16205A]">{c.name}</h1>
            <p className="text-sm text-slate-500 mt-2 font-mono">
              {c.company || "—"} · {c.email || "no email"} · {c.phone || "no phone"}
            </p>
          </div>
          <div className="flex items-center gap-4">
            <Button variant="ghost" data-testid="add-reminder-customer" onClick={() => setRemOpen(true)}>
              <BellRinging size={16} weight="fill" /> Reminder
            </Button>
            <div className="text-right rounded-xl bg-slate-50 px-5 py-3 border border-slate-200">
              <p className="font-mono text-[10px] uppercase text-slate-400 tracking-wide">Outstanding</p>
              <p className="font-heading font-extrabold text-2xl tabular" style={{ color: c.outstanding_balance > 0 ? "#B91C1C" : "#15803D" }}>
                {xcg(c.outstanding_balance)}
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="p-8 space-y-6">
        {c.address && <p className="text-sm text-slate-500 flex items-center gap-1.5"><MapPin size={15} weight="fill" className="text-[#F49B1C]" /> {c.address}</p>}
        {c.notes && <div className="card-soft p-4 text-sm text-slate-600"><span className="font-semibold text-[#16205A]">Notes:</span> {c.notes}</div>}

        <div className="flex gap-2 border-b border-slate-200">
          {[
            { k: "balances", label: `Invoices & Balances (${c.invoices.length})` },
            { k: "emails", label: `Email Log (${c.email_logs.length})` },
          ].map((t) => (
            <button
              key={t.k}
              data-testid={`tab-${t.k}`}
              onClick={() => setTab(t.k)}
              className={`px-4 py-2.5 font-semibold text-sm -mb-px border-b-2 transition-colors ${
                tab === t.k ? "border-[#F49B1C] text-[#16205A]" : "border-transparent text-slate-400 hover:text-slate-600"
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>

        {tab === "balances" && (
          <div className="space-y-4">
            <div className="flex justify-end">
              <Button data-testid="add-invoice-button" onClick={() => setInvOpen(true)}>
                <Plus size={16} weight="bold" /> New Invoice
              </Button>
            </div>
            <div className="card-soft overflow-hidden">
              <table className="w-full">
                <thead>
                  <tr className="bg-slate-50 border-b border-slate-200 text-slate-500 font-mono text-xs uppercase tracking-wide">
                    <th className="text-left px-5 py-3.5 font-semibold">Invoice #</th>
                    <th className="text-left px-5 py-3.5 font-semibold">Description</th>
                    <th className="text-right px-5 py-3.5 font-semibold">Amount</th>
                    <th className="text-right px-5 py-3.5 font-semibold">Paid</th>
                    <th className="text-right px-5 py-3.5 font-semibold">Balance</th>
                    <th className="text-center px-5 py-3.5 font-semibold">Status</th>
                    <th className="px-5 py-3.5"></th>
                  </tr>
                </thead>
                <tbody>
                  {c.invoices.map((i) => {
                    const bal = i.amount - (i.amount_paid || 0);
                    return (
                      <tr key={i.id} data-testid={`invoice-row-${i.id}`} className="border-b border-slate-100 last:border-0">
                        <td className="px-5 py-4 font-mono text-sm font-semibold text-[#16205A]">{i.invoice_number}</td>
                        <td className="px-5 py-4 text-sm text-slate-500">{i.description || "—"}</td>
                        <td className="px-5 py-4 text-right font-mono text-sm tabular">{xcg(i.amount)}</td>
                        <td className="px-5 py-4 text-right font-mono text-sm tabular text-[#15803D]">{xcg(i.amount_paid || 0)}</td>
                        <td className="px-5 py-4 text-right font-mono text-sm tabular font-semibold">{xcg(bal)}</td>
                        <td className="px-5 py-4 text-center"><StatusBadge status={i.status} /></td>
                        <td className="px-5 py-4 text-right whitespace-nowrap">
                          {bal > 0.001 && (
                            <button data-testid={`pay-invoice-${i.id}`} onClick={() => setPayOpen(i)} className="font-semibold text-xs rounded-md border border-slate-300 px-2.5 py-1 hover:bg-[#F49B1C] hover:text-white hover:border-[#F49B1C] mr-2 transition-colors">
                              PAY
                            </button>
                          )}
                          <button data-testid={`delete-invoice-${i.id}`} onClick={() => delInvoice(i.id)} className="text-slate-300 hover:text-red-500 align-middle transition-colors">
                            <Trash size={16} weight="bold" />
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                  {c.invoices.length === 0 && (
                    <tr><td colSpan={7} className="px-5 py-12 text-center text-sm text-slate-400">No invoices yet.</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {tab === "emails" && (
          <div className="space-y-4">
            <div className="flex justify-end">
              <Button data-testid="add-email-button" onClick={() => setEmailOpen(true)}>
                <Plus size={16} weight="bold" /> Log Email
              </Button>
            </div>
            <div className="space-y-3">
              {c.email_logs.map((e) => (
                <div key={e.id} data-testid={`email-log-${e.id}`} className="card-soft p-5">
                  <div className="flex items-start justify-between">
                    <div className="flex items-center gap-3">
                      <div className={`p-2 rounded-lg ${e.direction === "sent" ? "bg-orange-100" : "bg-indigo-50"}`}>
                        {e.direction === "sent" ? <ArrowUp size={16} weight="bold" color="#E0890F" /> : <ArrowDown size={16} weight="bold" color="#3730A3" />}
                      </div>
                      <div>
                        <p className="font-semibold text-sm text-slate-800 flex items-center gap-2"><EnvelopeSimple size={14} weight="fill" className="text-slate-400" /> {e.subject}</p>
                        <p className="font-mono text-xs text-slate-400">
                          {e.direction === "sent" ? "Sent to" : "Received from"} {e.contact || "—"} · {new Date(e.logged_date).toLocaleDateString()}
                        </p>
                      </div>
                    </div>
                    <button data-testid={`delete-email-${e.id}`} onClick={() => delEmail(e.id)} className="text-slate-300 hover:text-red-500 transition-colors">
                      <Trash size={16} weight="bold" />
                    </button>
                  </div>
                  {e.body && <p className="text-sm text-slate-600 mt-3 whitespace-pre-wrap border-t border-slate-100 pt-3">{e.body}</p>}
                </div>
              ))}
              {c.email_logs.length === 0 && <p className="text-sm text-slate-400 text-center py-12">No email conversations logged yet.</p>}
            </div>
          </div>
        )}
      </div>

      <Modal open={invOpen} onClose={() => setInvOpen(false)} title="New Invoice">
        <form onSubmit={saveInvoice} className="space-y-4">
          <Field label="Invoice Number *" data-testid="invoice-number-input" required value={inv.invoice_number} onChange={(e) => setInv({ ...inv, invoice_number: e.target.value })} />
          <Field label="Description" value={inv.description} onChange={(e) => setInv({ ...inv, description: e.target.value })} />
          <div className="grid grid-cols-2 gap-3">
            <Field label="Amount (XCG) *" type="number" step="0.01" data-testid="invoice-amount-input" required value={inv.amount} onChange={(e) => setInv({ ...inv, amount: e.target.value })} />
            <Field label="Due Date" type="date" value={inv.due_date} onChange={(e) => setInv({ ...inv, due_date: e.target.value })} />
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <Button type="button" variant="ghost" onClick={() => setInvOpen(false)}>Cancel</Button>
            <Button type="submit" data-testid="save-invoice-button">Create</Button>
          </div>
        </form>
      </Modal>

      <Modal open={!!payOpen} onClose={() => setPayOpen(null)} title="Record Payment">
        {payOpen && (
          <form onSubmit={savePayment} className="space-y-4">
            <p className="text-sm text-slate-600">Invoice <strong className="text-[#16205A]">{payOpen.invoice_number}</strong> · Balance <span className="font-mono">{xcg(payOpen.amount - (payOpen.amount_paid || 0))}</span></p>
            <Field label="Payment Amount (XCG) *" type="number" step="0.01" data-testid="payment-amount-input" required value={pay.amount} onChange={(e) => setPay({ ...pay, amount: e.target.value })} />
            <Field label="Note" value={pay.note} onChange={(e) => setPay({ ...pay, note: e.target.value })} />
            <div className="flex justify-end gap-2 pt-2">
              <Button type="button" variant="ghost" onClick={() => setPayOpen(null)}>Cancel</Button>
              <Button type="submit" data-testid="save-payment-button">Record</Button>
            </div>
          </form>
        )}
      </Modal>

      <Modal open={emailOpen} onClose={() => setEmailOpen(false)} title="Log Email Conversation">
        <form onSubmit={saveEmail} className="space-y-4">
          <Select label="Direction" data-testid="email-direction-select" value={email.direction} onChange={(e) => setEmail({ ...email, direction: e.target.value })}>
            <option value="received">Received from customer</option>
            <option value="sent">Sent to customer</option>
          </Select>
          <Field label="Contact (email/name)" value={email.contact} onChange={(e) => setEmail({ ...email, contact: e.target.value })} />
          <Field label="Subject *" data-testid="email-subject-input" required value={email.subject} onChange={(e) => setEmail({ ...email, subject: e.target.value })} />
          <TextArea label="Message / Notes" data-testid="email-body-input" value={email.body} onChange={(e) => setEmail({ ...email, body: e.target.value })} />
          <div className="flex justify-end gap-2 pt-2">
            <Button type="button" variant="ghost" onClick={() => setEmailOpen(false)}>Cancel</Button>
            <Button type="submit" data-testid="save-email-button">Save Log</Button>
          </div>
        </form>
      </Modal>

      <Modal open={remOpen} onClose={() => setRemOpen(false)} title={`Reminder · ${c.name}`}>
        <form onSubmit={saveReminder} className="space-y-4">
          <Field label="Title *" data-testid="customer-reminder-title" required value={rem.title} onChange={(e) => setRem({ ...rem, title: e.target.value })} placeholder="e.g. Follow up on quote" />
          <div className="grid grid-cols-2 gap-3">
            <Select label="Type" value={rem.kind} onChange={(e) => setRem({ ...rem, kind: e.target.value })}>
              <option value="follow_up">Follow up</option>
              <option value="payment">Payment</option>
              <option value="email">Email</option>
              <option value="general">General</option>
            </Select>
            <Field label="Due Date *" type="date" data-testid="customer-reminder-date" required value={rem.due_date} onChange={(e) => setRem({ ...rem, due_date: e.target.value })} />
          </div>
          <TextArea label="Note" value={rem.note} onChange={(e) => setRem({ ...rem, note: e.target.value })} />
          <div className="flex justify-end gap-2 pt-2">
            <Button type="button" variant="ghost" onClick={() => setRemOpen(false)}>Cancel</Button>
            <Button type="submit" data-testid="save-customer-reminder">Set Reminder</Button>
          </div>
        </form>
      </Modal>
    </div>
  );
}
